import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { resolveSite } from "../lib/siteConfig";
import { usePublicData } from "../hooks/usePublicData";
import { useLanguage } from "../context/LanguageContext";
import SiteLogo from "./SiteLogo";
import { pub } from "@/lib/pubThemeClasses";

export default function Footer() {
  const { settings } = usePublicData();
  const siteConfig = resolveSite(settings);
  const year = new Date().getFullYear();
  const { t } = useLanguage();

  return (
    <footer data-testid="site-footer" className={`border-t bg-surface-1 ${pub.borderSubtle}`}>
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5 space-y-6">
          <SiteLogo site={siteConfig} size="md" textClassName="text-xl text-neutral-900 dark:text-white" />
          <p className="font-sans text-soft text-sm leading-relaxed max-w-md">{t("footer.tagline")}</p>
          <Link
            to="/teklif-al#teklif-yukle"
            data-testid="footer-quote-button"
            className="inline-flex items-center gap-2 bg-[#FF4F00] hover:bg-[#E64700] text-black font-bold uppercase tracking-wider text-xs px-5 py-3 transition-all hover:-translate-y-0.5"
          >
            {t("header.uploadModel")} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-sans text-xs font-semibold tracking-wide text-[#FF4F00] mb-5">{t("footer.pages")}</h4>
          <ul className="space-y-3 text-sm">
            <li>
              <Link to="/hizmetler" className={pub.linkMuted}>
                {t("nav.services")}
              </Link>
            </li>
            <li>
              <Link to="/malzemeler" className={pub.linkMuted}>
                {t("nav.materials")}
              </Link>
            </li>
            <li>
              <Link to="/portfoy" className={pub.linkMuted}>
                {t("nav.portfolio")}
              </Link>
            </li>
            <li>
              <Link to="/hakkimizda" className={pub.linkMuted}>
                {t("nav.about")}
              </Link>
            </li>
            <li>
              <Link to="/iletisim" className={pub.linkMuted}>
                {t("nav.contact")}
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="font-sans text-xs font-semibold tracking-wide text-[#FF4F00] mb-5">{t("footer.contactSection")}</h4>
          <ul className="space-y-4 text-sm text-soft">
            {siteConfig.address?.trim() ? (
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-neutral-400 dark:text-[#8f877c] shrink-0" />
                <span>{siteConfig.address.trim()}</span>
              </li>
            ) : null}
            <li className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-neutral-400 dark:text-[#8f877c] shrink-0" />
              <a href={`tel:${siteConfig.phone}`} className={pub.linkMuted}>
                {siteConfig.phone}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-neutral-400 dark:text-[#8f877c] shrink-0" />
              <a href={`mailto:${siteConfig.email}`} className={pub.linkMuted}>
                {siteConfig.email}
              </a>
            </li>
            <li className="font-mono text-xs tracking-wide uppercase text-neutral-600 dark:text-[#a89f93] pt-2">{siteConfig.workingHours}</li>
          </ul>
        </div>
      </div>

      <div className={`border-t ${pub.borderSubtle}`}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-8 flex flex-col gap-6">
          <ul className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-[0.15em] text-neutral-600 dark:text-[#a89f93]">
            <li>
              <Link to="/gizlilik" className={pub.hoverText}>
                {t("footer.legalPrivacy")}
              </Link>
            </li>
            <li>
              <Link to="/kvkk" className={pub.hoverText}>
                {t("footer.legalKvkk")}
              </Link>
            </li>
            <li>
              <Link to="/kullanim-sartlari" className={pub.hoverText}>
                {t("footer.legalTerms")}
              </Link>
            </li>
          </ul>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <p className="text-xs font-mono text-neutral-600 dark:text-[#a89f93] uppercase tracking-[0.18em]">
              © {year} {siteConfig.brand} • {t("footer.copyright")}
            </p>
            <p className="text-xs font-mono text-neutral-600 dark:text-[#a89f93] uppercase tracking-[0.18em]">{t("footer.madeIn")}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
